// TelcoTrack — D5 occupancy workbook: physical sheets + port address list.

import { buildRayTr3D5Grid } from './rozvadec-occupancy-schema.raytr3-grid';
import {
  occupancySheetPortLine,
  occupancySheetPositionPorts,
} from './rozvadec-occupancy-schema.sheets';
import type {
  RozvadecOccupancyGenerateResult,
  RozvadecOccupancyPortCell,
  RozvadecOccupancySheet,
  RozvadecOccupancySheetLayout,
} from './rozvadec-occupancy-schema.types';

export const ROZVADEC_OCCUPANCY_SCHEMA_SHEET_NAME = 'D5';
export const ROZVADEC_OCCUPANCY_SCHEMA_BLOCKERS_SHEET_NAME = 'Blokátory';
export const ROZVADEC_OCCUPANCY_SCHEMA_PORTS_SHEET_NAME = 'Porty';

export const ROZVADEC_OCCUPANCY_SCHEMA_TABLE_HEADERS = [
  'Řada',
  'Pozice',
  'Kazeta',
  'Port / svár',
  'Zdroj',
  'Cíl',
  'Odkaz',
  'Stav',
  'Barva',
  'Logické č.',
] as const;

export type RozvadecOccupancySchemaTableHeader =
  (typeof ROZVADEC_OCCUPANCY_SCHEMA_TABLE_HEADERS)[number];

export type RozvadecOccupancySchemaTableKind =
  | 'Konektor'
  | 'Svár'
  | 'Splitter IN'
  | 'Splitter OUT'
  | 'Napřímo'
  | 'Port';

export type RozvadecOccupancySchemaTableRow = {
  cassette: string;
  rowPosition: string;
  kind: RozvadecOccupancySchemaTableKind;
  port: string;
  rearTermination: string;
  incomingColor: string;
  logicalFiber: string;
  frontConnection: string;
  splitter: string;
  splitterPort: string;
  building: string;
  outgoingFiber: string;
  outgoingColor: string;
};

export type RozvadecOccupancySchemaTableMeta = {
  title: string;
  printName: string;
  rendererId: string;
  sapCode: string;
  typeId: string;
  networkRole: string;
  locality: string;
  configuration: string;
  exportable: boolean;
  generatedAt: string | null;
};

export type RozvadecOccupancySchemaWorkbookSheet = {
  name: string;
  aoa: (string | number)[][];
  headerRowIndex: number;
  layout?: RozvadecOccupancySheetLayout;
};

export type RozvadecOccupancySchemaTable = {
  sheetName: typeof ROZVADEC_OCCUPANCY_SCHEMA_SHEET_NAME;
  blockersSheetName: typeof ROZVADEC_OCCUPANCY_SCHEMA_BLOCKERS_SHEET_NAME;
  meta: RozvadecOccupancySchemaTableMeta;
  headers: readonly RozvadecOccupancySchemaTableHeader[];
  rows: RozvadecOccupancySchemaTableRow[];
  /** First physical / primary D5 sheet. */
  aoa: (string | number)[][];
  headerRowIndex: number;
  sheets: RozvadecOccupancySchemaWorkbookSheet[];
  physicalSheets: RozvadecOccupancySheet[];
  blockersAoa: (string | number)[][];
};

const EMPTY = '—';

function dash(value: string | number | null | undefined): string {
  if (value == null || value === '') return EMPTY;
  return String(value);
}

function excelName(value: string, used: Set<string>): string {
  const raw = value.replace(/[\\/*?[\]:]/g, ' ').trim().slice(0, 31) || 'List';
  let name = raw;
  let n = 2;
  while (used.has(name)) {
    const suffix = ` ${n}`;
    name = `${raw.slice(0, 31 - suffix.length)}${suffix}`;
    n += 1;
  }
  used.add(name);
  return name;
}

function physicalSheetAoa(sheet: RozvadecOccupancySheet, meta: RozvadecOccupancySchemaTableMeta): {
  aoa: (string | number)[][];
  layout?: RozvadecOccupancySheetLayout;
} {
  if (sheet.kind === 'ray-stand') {
    return buildRayTr3D5Grid(sheet);
  }
  return { aoa: physicalMukSheetAoa(sheet, meta) };
}

function physicalMukSheetAoa(sheet: RozvadecOccupancySheet, meta: RozvadecOccupancySchemaTableMeta): (string | number)[][] {
  const role = sheet.networkRole === 'none' ? EMPTY : sheet.networkRole;
  const aoa: (string | number)[][] = [
    ['Schéma osazení D5', sheet.printName, sheet.cabinetTypeLabel, sheet.configuration],
    ['SAP', sheet.sapCode, 'Funkce', role, 'Lokalita', sheet.locality],
    [
      'Řada',
      `${sheet.rowLabel}${sheet.sideLabel ? ` · ${sheet.sideLabel}` : ''}`,
      'Pohled',
      'přední',
      'List',
      `${sheet.sheetIndex} / ${sheet.sheetCount}`,
      'Vygenerováno',
      meta.generatedAt ?? EMPTY,
    ],
    [],
    ['Pozice', 'Kazeta', 'Porty / svary', 'Cíle', 'Odkazy', 'Typ / trasa / kabel', 'Stav'],
  ];
  for (const slot of sheet.positions.filter((item) => item.render)) {
    const { labels, targets } = occupancySheetPositionPorts(slot);
    const cassetteLabel = slot.empty ? slot.cassette?.printLabel ?? 'Volná pozice' : slot.cassette?.printLabel ?? EMPTY;
    const status = slot.empty
      ? 'volno'
      : slot.cassette?.ports.some((item) => item.status === 'obsazeno')
        ? 'obsazeno'
        : slot.cassette?.ports.every((item) => item.status === 'rez.')
          ? 'rez.'
          : 'volno';
    aoa.push([
      slot.scaleLabels.join(' / '),
      cassetteLabel,
      occupancySheetPortLine(labels, 'printLabel'),
      occupancySheetPortLine(targets.length ? targets : labels, 'target'),
      occupancySheetPortLine(slot.cassette?.ports ?? [], 'hopRef'),
      slot.isCassetteStart ? slot.cassette?.routeLabel ?? '' : '',
      status,
    ]);
  }
  aoa.push([]);
  aoa.push(['Splittery ve dveřích']);
  if (sheet.doorSplitters.length === 0) {
    aoa.push([EMPTY, 'Žádný 1:32 ve dveřích']);
  } else {
    aoa.push(['Splitter', 'Poměr', 'IN', 'OUT → cíl']);
    for (const door of sheet.doorSplitters) {
      aoa.push([
        door.printLabel,
        door.ratio,
        door.input.hopRef ?? door.input.printLabel,
        door.outputs
          .filter((item) => item.hopRef || item.status === 'obsazeno')
          .map((item) => item.hopRef ?? `${item.printLabel} ${item.target}`)
          .join(' | ') || EMPTY,
      ]);
    }
  }
  aoa.push([]);
  aoa.push(['Odkazy']);
  for (const ref of sheet.references) aoa.push([ref]);
  if (sheet.references.length === 0) aoa.push([EMPTY]);
  aoa.push([]);
  aoa.push(['Legenda', 'obsazeno / rez. / volno / plán', 'Žádné křížící čáry — jen odkazy']);
  for (const note of sheet.notes) aoa.push(['Poznámka', note]);
  return aoa;
}

function portRowsFromSheet(sheet: RozvadecOccupancySheet): RozvadecOccupancySchemaTableRow[] {
  const rows: RozvadecOccupancySchemaTableRow[] = [];
  const pushPort = (port: RozvadecOccupancyPortCell, cassette: string, position: number, kind: RozvadecOccupancySchemaTableKind) => {
    rows.push({
      cassette,
      rowPosition: `${sheet.rowLabel} / pozice ${position}`,
      kind,
      port: port.printLabel,
      rearTermination: port.source,
      incomingColor: port.colorLabel ?? EMPTY,
      logicalFiber: port.logicalFiber ?? EMPTY,
      frontConnection: port.hopRef ?? port.target,
      splitter: port.printLabel.includes('SPL') || port.printLabel.startsWith('OUT') ? port.printLabel : EMPTY,
      splitterPort: port.printLabel.startsWith('OUT') || port.printLabel.includes('IN') ? port.printLabel : EMPTY,
      building: port.target,
      outgoingFiber: port.status,
      outgoingColor: EMPTY,
    });
  };
  for (const slot of sheet.positions) {
    if (!slot.cassette || !slot.isCassetteStart) continue;
    const kind: RozvadecOccupancySchemaTableKind =
      slot.cassette.kind === 'KM4' || slot.cassette.kind === 'KM6'
        ? 'Svár'
        : slot.cassette.kind === 'KM5_SPLITTER'
          ? 'Splitter OUT'
          : 'Konektor';
    for (const port of slot.cassette.ports) {
      pushPort(port, slot.cassette.printLabel, slot.cassette.startPosition, kind);
    }
  }
  for (const door of sheet.doorSplitters) {
    pushPort(door.input, 'dveře', 0, 'Splitter IN');
    for (const out of door.outputs) pushPort(out, 'dveře', 0, 'Splitter OUT');
  }
  return rows;
}

function cellsOf(row: RozvadecOccupancySchemaTableRow): string[] {
  const rada = row.rowPosition.includes('/') ? row.rowPosition.split('/')[0].trim() : row.rowPosition;
  const pozice = row.rowPosition.replace(/.*pozice\s+/, '').trim();
  return [
    rada,
    pozice,
    row.cassette,
    row.port,
    row.rearTermination,
    row.building,
    row.frontConnection,
    row.outgoingFiber || EMPTY,
    row.incomingColor,
    row.logicalFiber,
  ];
}

function portAoa(rows: RozvadecOccupancySchemaTableRow[]): (string | number)[][] {
  return [[...ROZVADEC_OCCUPANCY_SCHEMA_TABLE_HEADERS], ...rows.map(cellsOf)];
}

export function buildRozvadecOccupancySchemaRows(
  result: RozvadecOccupancyGenerateResult,
): RozvadecOccupancySchemaTableRow[] {
  const seen = new Set<string>();
  const rows: RozvadecOccupancySchemaTableRow[] = [];
  for (const sheet of result.schema.sheets) {
    for (const row of portRowsFromSheet(sheet)) {
      const key = `${row.rowPosition}|${row.cassette}|${row.port}|${row.kind}`;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push(row);
    }
  }
  return rows;
}

export function buildRozvadecOccupancySchemaTable(
  result: RozvadecOccupancyGenerateResult,
  options?: { generatedAt?: string | null },
): RozvadecOccupancySchemaTable {
  const meta: RozvadecOccupancySchemaTableMeta = {
    title: `Schéma osazení D5 — ${result.schema.printName}`,
    printName: result.schema.printName,
    rendererId: result.schema.rendererId,
    sapCode: result.schema.sapCode ?? EMPTY,
    typeId: result.schema.typeId ?? EMPTY,
    networkRole: result.schema.networkRole === 'none' ? EMPTY : result.schema.networkRole,
    locality: result.schema.locality ?? EMPTY,
    configuration: result.schema.configuration ?? EMPTY,
    exportable: result.exportable,
    generatedAt: options?.generatedAt ?? null,
  };
  const blockersAoa: (string | number)[][] =
    result.blockers.length === 0
      ? [['Kód', 'Cesta', 'Zpráva'], ['—', '—', 'Žádné blokátory']]
      : [
          ['Kód', 'Cesta', 'Zpráva'],
          ...result.blockers.map((item) => [item.code, item.path, item.message]),
        ];

  const physical = result.schema.sheets;
  const rows = buildRozvadecOccupancySchemaRows(result);
  const used = new Set<string>();
  const sheets: RozvadecOccupancySchemaWorkbookSheet[] = [];
  if (physical.length === 0) {
    const aoa: (string | number)[][] = [
      ['Schéma osazení D5', meta.printName],
      ['Renderer', meta.rendererId, 'SAP', meta.sapCode, 'Typ', meta.typeId],
      [],
      [...ROZVADEC_OCCUPANCY_SCHEMA_TABLE_HEADERS],
      ...rows.map(cellsOf),
    ];
    sheets.push({ name: excelName(ROZVADEC_OCCUPANCY_SCHEMA_SHEET_NAME, used), aoa, headerRowIndex: 3 });
    return {
      sheetName: ROZVADEC_OCCUPANCY_SCHEMA_SHEET_NAME,
      blockersSheetName: ROZVADEC_OCCUPANCY_SCHEMA_BLOCKERS_SHEET_NAME,
      meta,
      headers: ROZVADEC_OCCUPANCY_SCHEMA_TABLE_HEADERS,
      rows,
      aoa,
      headerRowIndex: 3,
      sheets,
      physicalSheets: [],
      blockersAoa,
    };
  }

  physical.forEach((sheet, index) => {
    const name = index === 0
      ? excelName(ROZVADEC_OCCUPANCY_SCHEMA_SHEET_NAME, used)
      : excelName(`${sheet.rowLabel}${sheet.sideLabel ? ` ${sheet.sideLabel}` : ''}`, used);
    const built = physicalSheetAoa(sheet, meta);
    sheets.push({
      name,
      aoa: built.aoa,
      headerRowIndex: sheet.kind === 'ray-stand' ? 1 : 4,
      layout: built.layout,
    });
  });
  sheets.push({
    name: excelName(ROZVADEC_OCCUPANCY_SCHEMA_PORTS_SHEET_NAME, used),
    aoa: portAoa(rows),
    headerRowIndex: 0,
  });

  return {
    sheetName: ROZVADEC_OCCUPANCY_SCHEMA_SHEET_NAME,
    blockersSheetName: ROZVADEC_OCCUPANCY_SCHEMA_BLOCKERS_SHEET_NAME,
    meta,
    headers: ROZVADEC_OCCUPANCY_SCHEMA_TABLE_HEADERS,
    rows,
    aoa: sheets[0]?.aoa ?? [],
    headerRowIndex: sheets[0]?.headerRowIndex ?? 4,
    sheets,
    physicalSheets: physical,
    blockersAoa,
  };
}
